import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { PDialogFooterBtnActionType, PDialogFooterBtnConfig } from '@frontend-site/ui/components';
import { ChangePasswordService, UserService } from '@frontend-site/users/domain';
import { take } from 'rxjs';

/**
 * "Change password" from the Security tab. Runs the same client-side rules the profile save used,
 * then commits immediately through `POST /UM/password/change` so the IDP owns the write. Closes
 * with `true` on success, `undefined` on cancel; stays open on a server-side error with the server
 * message in `errMsg`.
 */
@Component({
    selector: 'p-change-password-dialog',
    templateUrl: './change-password-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangePasswordDialogComponent {
    form: UntypedFormGroup;
    errMsg = '';
    errMsgInvalid = '';
    changing = false;

    constructor(
        fb: UntypedFormBuilder,
        private readonly dialogRef: MatDialogRef<ChangePasswordDialogComponent, boolean | undefined>,
        private readonly userService: UserService,
        private readonly changePasswordService: ChangePasswordService,
        private readonly cd: ChangeDetectorRef
    ) {
        this.form = fb.group({ password: [''], newPassword: [''], reNewPassword: [''] });
    }

    get footerConfig(): PDialogFooterBtnConfig[] {
        return [
            {
                text: 'UserProfile.Security.ChangePassword.apply',
                click: (): void => this.apply(),
                disabled: this.changing,
                automationId: 'change_password_apply'
            },
            { type: PDialogFooterBtnActionType.Close }
        ];
    }

    apply(): void {
        const { password, newPassword, reNewPassword } = this.form.value;
        let ok: boolean;
        [ok, this.errMsg, this.errMsgInvalid] = this.userService.validatePassword(password, newPassword, reNewPassword);
        if (!ok) {
            return;
        }
        if (!newPassword) {
            // Every field cleared → nothing to commit, close with no change.
            this.dialogRef.close(undefined);
            return;
        }
        this.changing = true;
        this.errMsg = '';
        this.errMsgInvalid = '';
        this.changePasswordService
            .change(password, newPassword)
            .pipe(take(1))
            .subscribe({
                next: () => this.dialogRef.close(true),
                error: (err) => {
                    this.errMsg = err?.error?.error || 'UserProfile.Security.ChangePassword.failed';
                    this.changing = false;
                    this.cd.markForCheck();
                }
            });
    }
}
